import React, { useEffect, useState } from "react";
import { NavLink, Routes, Route, Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Mail, Calendar, ClipboardCheck, Users, Pencil, Save, X, Settings as SettingsIcon, Shield, FileDown, ArrowLeft, CheckCircle2, Clock, ChevronRight, Send, Eye } from "lucide-react";
import Raters360 from "../components/Raters360";
import PasswordInput from "../components/PasswordInput";

const PROGRAM_OPTIONS = ["Executive Coaching", "Coaching d'équipe", "Formation", "Outplacement"];
const DEFAULT_PROGRAM = PROGRAM_OPTIONS[0];

function Shell({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const nav = useNavigate();

  const links = [
    { to: "/admin", label: "Portail des coachés", icon: Users, exact: true },
    { to: "/admin/sessions", label: "Sessions", icon: Calendar },
    { to: "/admin/assignments", label: "Assigner tests", icon: ClipboardCheck },
    { to: "/admin/messages", label: "Messages", icon: Mail },
    { to: "/admin/parametres", label: "Paramètres", icon: SettingsIcon },
  ];

  return (
    <div className="min-h-screen flex bg-navy">
      <aside className="hidden md:flex flex-col w-72 border-r border-ivory/10 bg-navy p-8 sticky top-0 h-screen">
        <div className="flex items-center gap-2 mb-2">
          <Shield size={14} className="text-brand" />
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand">— Administration</div>
        </div>
        <div className="font-serif text-2xl text-ivory font-light leading-tight">{user?.name}</div>
        <div className="text-xs text-ivory/55">{user?.email}</div>
        <nav className="mt-12 space-y-1 flex-1">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.exact} data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s/g,'-')}`} className={({ isActive }) => `flex items-center gap-3 px-3 py-3 text-sm transition-colors ${isActive ? "bg-brand/20 text-brand border-l-2 border-brand" : "text-ivory/70 hover:text-ivory hover:bg-ivory/5 border-l-2 border-transparent"}`}>
              <l.icon size={15} /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto pt-8 border-t border-ivory/10">
          <div className="flex items-center gap-2 mb-4 font-mono text-[11px]">
            <button onClick={() => setLang("fr")} className={lang === "fr" ? "text-brand" : "text-ivory/50"}>FR</button>
            <span className="text-ivory/30">/</span>
            <button onClick={() => setLang("en")} className={lang === "en" ? "text-brand" : "text-ivory/50"}>EN</button>
          </div>
          <button onClick={async () => { await logout(); nav("/"); }} className="flex items-center gap-2 text-xs text-ivory/60 hover:text-brand">
            <LogOut size={14} /> {t.nav.logout}
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 p-6 md:p-12 max-w-[1300px] page-enter bg-ivory text-ink">{children}</main>
    </div>
  );
}

function CoacheeRow({ c, onDelete, onResendWelcome }) {
  return (
    <div className="border-b border-black/10 last:border-b-0" data-testid={`coachee-${c.id}`}>
      <div className="p-5 md:p-6 flex items-center justify-between gap-6">
        <Link to={`/admin/coache/${c.id}`} className="min-w-0 flex-1 group">
          <div className="font-serif text-xl text-navy group-hover:text-brand transition-colors">
            {c.gender === "madame" ? "Mme " : (c.gender === "monsieur" ? "M. " : "")}{c.first_name ? `${c.first_name} ` : ""}{c.name}
          </div>
          <div className="text-xs text-ink/55 truncate">
            {c.email} · {c.program}
            {c.company && <span className="text-brand font-mono uppercase tracking-[0.14em] ml-2">— {c.company}</span>}
            {c.function && <span className="text-ink/45 ml-2">· {c.function}</span>}
          </div>
        </Link>
        <div className="flex items-center gap-2 flex-none">
          <Link
            to={`/admin/coache/${c.id}`}
            data-testid={`detail-${c.id}`}
            className="inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.18em] font-mono border border-black/10 text-ink/70 hover:border-brand hover:text-brand transition-colors"
          >
            Suivi <ChevronRight size={12} />
          </Link>
          <button
            onClick={() => onResendWelcome(c)}
            title="Renvoyer l'email de bienvenue avec de nouveaux identifiants"
            className="p-2 text-ink/40 hover:text-brand transition-colors"
            data-testid={`resend-welcome-${c.id}`}
          >
            <Send size={16} />
          </button>
          <button onClick={() => onDelete(c.id)} className="p-2 text-ink/40 hover:text-destructive" data-testid={`delete-coachee-${c.id}`}><Trash2 size={16} /></button>
        </div>
      </div>
    </div>
  );
}


function Coachees() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", first_name: "", program: DEFAULT_PROGRAM, password: "", company: "", function: "", gender: "" });
  const [created, setCreated] = useState(null);

  const load = () => api.get("/admin/coachees").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/coachees", form);
      setCreated(data);
      setForm({ email: "", name: "", first_name: "", program: DEFAULT_PROGRAM, password: "", company: "", function: "", gender: "" });
      setAdding(false);
      load();
      toast.success("Coaché créé");
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce coaché et toutes ses données ?")) return;
    await api.delete(`/admin/coachees/${id}`);
    load();
  };

  const resendWelcome = async (c) => {
    const label = `${c.first_name ? c.first_name + " " : ""}${c.name}`.trim();
    if (!window.confirm(`Renvoyer l'email d'ouverture de compte à ${label} ?\n\nLe mot de passe temporaire initial sera rappelé.`)) return;
    try {
      const { data } = await api.post(`/admin/coachees/${c.id}/resend-welcome`);
      setCreated({ user: c, temp_password: data.temp_password, email_sent: data.email_sent });
      if (data.email_sent) {
        toast.success(data.regenerated ? "Email renvoyé · nouveau mot de passe généré" : "Email d'ouverture de compte renvoyé");
      } else {
        toast.warning("Email non envoyé — Resend indisponible. Identifiants affichés ci-dessus.");
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-12">
        <div>
          <h1 className="font-serif text-4xl md:text-5xl font-light text-navy">Portail des coachés</h1>
        </div>
        <button data-testid="add-coachee" onClick={() => setAdding(true)} className="inline-flex items-center gap-2 bg-brand hover:bg-brandDark text-white px-5 py-3 text-xs uppercase tracking-[0.16em] font-mono">
          <Plus size={14} /> Ajouter
        </button>
      </div>

      {created && (
        <div data-testid="created-coachee" className="mb-6 border border-brand bg-brand/5 p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand mb-2">
            {created.email_sent === false ? "Identifiants — email non envoyé" : "Compte créé · email envoyé"}
          </div>
          <div className="text-sm">Email : <span className="font-mono">{created.user.email}</span></div>
          <div className="text-sm">Mot de passe temporaire : <span className="font-mono font-semibold">{created.temp_password}</span></div>
          <button onClick={() => setCreated(null)} className="mt-4 text-xs text-ink/60">Masquer</button>
        </div>
      )}

      {adding && (
        <form onSubmit={create} data-testid="coachee-form" className="border border-black/10 bg-white p-8 mb-8 grid grid-cols-12 gap-6">
          <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="col-span-12 md:col-span-2 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" data-testid="coachee-gender">
            <option value="">Genre…</option>
            <option value="madame">Madame</option>
            <option value="monsieur">Monsieur</option>
          </select>
          <input placeholder="Prénom" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="col-span-12 md:col-span-4 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" data-testid="coachee-first-name" />
          <input required placeholder="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="col-span-12 md:col-span-6 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" data-testid="coachee-name" />
          <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="col-span-12 md:col-span-6 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" data-testid="coachee-email" />
          <input placeholder="Entreprise" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="col-span-12 md:col-span-6 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" data-testid="coachee-company" />
          <input placeholder="Fonction" value={form.function} onChange={(e) => setForm({ ...form, function: e.target.value })} className="col-span-12 md:col-span-6 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" data-testid="coachee-function" />
          <select value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value })} className="col-span-12 md:col-span-6 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" data-testid="coachee-program">
            {PROGRAM_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <input placeholder="Mot de passe (auto si vide)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="col-span-12 md:col-span-6 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" />
          <div className="col-span-12 flex gap-3">
            <button type="submit" className="bg-brand text-white px-5 py-3 text-xs uppercase tracking-[0.16em] font-mono">Créer</button>
            <button type="button" onClick={() => setAdding(false)} className="border border-ink/20 px-5 py-3 text-xs uppercase tracking-[0.16em] font-mono">Annuler</button>
          </div>
        </form>
      )}

      <div className="divide-y divide-black/10 border border-black/10 bg-white">
        {items.length === 0 && <div className="p-8 text-ink/50">Aucun coaché</div>}
        {items.map((c) => (
          <CoacheeRow key={c.id} c={c} onDelete={remove} onResendWelcome={resendWelcome} />
        ))}
      </div>
    </div>
  );
}

function CoacheeDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", first_name: "", company: "", function: "", program: "", gender: "" });
  const [notes, setNotes] = useState("");
  const [notesUpdated, setNotesUpdated] = useState(null);
  const [savingNotes, setSavingNotes] = useState(false);
  const [initialPw, setInitialPw] = useState(null); // { temp_password, has_password } | null
  const [loadingPw, setLoadingPw] = useState(false);

  const showInitialPassword = async () => {
    if (initialPw) { setInitialPw(null); return; } // toggle off
    setLoadingPw(true);
    try {
      const { data } = await api.get(`/admin/coachees/${id}/initial-password`);
      if (!data.has_password) {
        toast.info("Le coaché a déjà défini son mot de passe — utilisez « Renvoyer l'email d'ouverture » pour en générer un nouveau.");
      } else {
        setInitialPw(data);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingPw(false); }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/coachees/${id}/detail`);
      setData(data);
      setForm({
        name: data.user.name || "",
        first_name: data.user.first_name || "",
        company: data.user.company || "",
        function: data.user.function || "",
        program: data.user.program || "",
        gender: data.user.gender || "",
      });
      setNotes(data.notes.notes || "");
      setNotesUpdated(data.notes.updated_at);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [id]);

  const saveProfile = async () => {
    try {
      await api.patch(`/admin/coachees/${id}`, form);
      toast.success("Profil mis à jour");
      setEditing(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const { data } = await api.put(`/admin/coachees/${id}/notes`, { notes });
      setNotesUpdated(data.updated_at);
      toast.success("Notes enregistrées");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingNotes(false); }
  };

  const downloadPdf = async (assignmentId) => {
    try {
      const res = await api.get(`/admin/coachees/${id}/tests/${assignmentId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteAssignment = async (assignmentId, label) => {
    if (!window.confirm(`Supprimer le test « ${label} » affecté à ce coaché ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/coachees/${id}/tests/${assignmentId}`);
      toast.success("Test supprimé");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return null;
  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "—";
  const fmtDt = (iso) => iso ? new Date(iso).toLocaleString("fr-FR") : "—";

  return (
    <div data-testid="admin-coachee-detail">
      <button onClick={() => nav("/admin")} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-mono text-ink/55 hover:text-brand mb-10" data-testid="admin-back-coachees">
        <ArrowLeft size={14} /> Retour aux coachés
      </button>

      <div className="flex items-start justify-between gap-6 mb-3">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand mb-3">— Suivi individuel</div>
          <h1 className="font-serif text-4xl md:text-5xl font-light text-navy">
            {data.user.gender === "madame" ? "Mme " : (data.user.gender === "monsieur" ? "M. " : "")}{data.user.first_name ? `${data.user.first_name} ` : ""}{data.user.name}
          </h1>
          <div className="text-ink/65 mt-2">{data.user.function || <span className="italic text-ink/40">Fonction non renseignée</span>}{data.user.company && <span className="text-brand"> — {data.user.company}</span>}</div>
          <div className="text-xs text-ink/45 font-mono mt-1">{data.user.email} · {data.user.program}</div>
        </div>
        <div className="flex items-center gap-2 flex-none">
          <button onClick={showInitialPassword} disabled={loadingPw} data-testid="show-initial-password" className="inline-flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-[0.18em] font-mono border border-black/10 text-ink/70 hover:border-brand hover:text-brand transition-colors disabled:opacity-40">
            <Eye size={12} /> {initialPw ? "Masquer" : "Voir mot de passe initial"}
          </button>
          <button onClick={() => setEditing(!editing)} data-testid="edit-profile-toggle" className={`inline-flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-[0.18em] font-mono transition-colors ${editing ? "bg-brand text-white" : "border border-black/10 text-ink/70 hover:border-brand hover:text-brand"}`}>
            <Pencil size={12} /> {editing ? "Annuler" : "Éditer profil"}
          </button>
        </div>
      </div>

      {initialPw && (
        <div className="mt-4 border border-brand bg-brand/5 px-5 py-4 flex items-center gap-4 flex-wrap" data-testid="initial-password-panel">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand">Mot de passe initial</span>
          <span className="font-mono text-sm font-semibold text-navy select-all">{initialPw.temp_password}</span>
          <span className="text-xs text-ink/50 italic">— sera invalidé dès le premier changement de mot de passe par le coaché.</span>
        </div>
      )}

      {editing && (
        <div className="bg-white border border-black/10 p-6 mt-6 mb-12 grid grid-cols-12 gap-4" data-testid="edit-profile-panel">
          <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="col-span-12 md:col-span-2 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" data-testid="edit-gender">
            <option value="">Genre…</option>
            <option value="madame">Madame</option>
            <option value="monsieur">Monsieur</option>
          </select>
          <input placeholder="Prénom" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="col-span-12 md:col-span-4 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" data-testid="edit-first-name" />
          <input placeholder="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="col-span-12 md:col-span-6 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" data-testid="edit-name" />
          <select value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value })} className="col-span-12 md:col-span-6 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" data-testid="edit-program">
            <option value="">Programme…</option>
            {PROGRAM_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <input placeholder="Entreprise" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="col-span-12 md:col-span-6 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" data-testid="edit-company" />
          <input placeholder="Fonction" value={form.function} onChange={(e) => setForm({ ...form, function: e.target.value })} className="col-span-12 md:col-span-6 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" data-testid="edit-function" />
          <div className="col-span-12">
            <button onClick={saveProfile} data-testid="edit-save" className="inline-flex items-center gap-2 bg-brand text-white px-5 py-3 text-xs uppercase tracking-[0.16em] font-mono"><Save size={12} /> Enregistrer</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14 mt-10">
        {[
          { label: "Progression", value: `${data.stats.progression}%` },
          { label: "Tests", value: `${data.stats.tests_completed} / ${data.stats.tests_total}` },
          { label: "Séances", value: data.stats.sessions_total },
          { label: "À venir", value: data.stats.sessions_upcoming },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-black/10 p-5">
            <div className="num-display text-3xl text-navy">{s.value}</div>
            <div className="text-[9px] uppercase tracking-[0.18em] font-mono text-ink/55 mt-2">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <section>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand mb-4">— Tests</div>
          <div className="bg-white border border-black/10 divide-y divide-black/10">
            {data.tests.length === 0 && <div className="p-5 text-sm text-ink/50">Aucun test</div>}
            {data.tests.map((t) => (
              <div key={t.id} className="p-5 flex items-center justify-between gap-4" data-testid={`admin-test-${t.id}`}>
                <div className="min-w-0">
                  <div className="font-serif text-base text-navy truncate">{t.template?.title_fr}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-ink/50 mt-1">
                    {t.status === "completed" ? `Terminé · ${fmt(t.completed_at)}` : "En cours"}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-none">
                  {t.result?.score != null && <span className="num-display text-lg text-navy">{t.result.score}<span className="text-ink/35 text-[10px]">%</span></span>}
                  {t.status === "completed" ? (
                    t.test_template_id === "360-competencies" ? (
                      <span className="text-[10px] uppercase tracking-[0.18em] font-mono text-ink/45 italic" title="Voir la section 360° plus bas pour les rapports PDF">
                        Voir 360° ci-dessous
                      </span>
                    ) : (
                      <button onClick={() => downloadPdf(t.id)} data-testid={`pdf-${t.id}`} title="Télécharger PDF" className="inline-flex items-center gap-1 px-3 py-2 text-[10px] uppercase tracking-[0.18em] font-mono border border-black/10 text-ink/70 hover:border-brand hover:text-brand">
                        <FileDown size={12} /> PDF
                      </button>
                    )
                  ) : <Clock size={16} className="text-ink/35" />}
                  <button
                    onClick={() => deleteAssignment(t.id, t.template?.title_fr || t.test_template_id)}
                    data-testid={`delete-assignment-${t.id}`}
                    title="Retirer ce test du coaché"
                    className="inline-flex items-center justify-center w-8 h-8 text-ink/40 hover:text-red-600 hover:bg-red-50 transition-colors border border-transparent hover:border-red-200"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand mb-4">— Séances</div>
          <div className="bg-white border border-black/10 divide-y divide-black/10">
            {data.sessions.length === 0 && <div className="p-5 text-sm text-ink/50">Aucune séance</div>}
            {data.sessions.map((s) => {
              const upcoming = s.starts_at >= new Date().toISOString();
              return (
                <div key={s.id} className="p-5" data-testid={`admin-session-${s.id}`}>
                  <div className="flex items-center justify-between gap-4 mb-1">
                    <div className="min-w-0">
                      <div className="font-serif text-base text-navy truncate">{s.title}</div>
                      <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-ink/50 mt-1">{fmt(s.starts_at)} · {s.duration_minutes} min{s.location ? ` · ${s.location}` : ""}</div>
                    </div>
                    <span className={`text-[9px] uppercase tracking-[0.18em] font-mono flex-none ${upcoming ? "text-brand" : "text-ink/40"}`}>
                      {upcoming ? "À venir" : "Passée"}
                    </span>
                  </div>
                  {s.notes && <p className="text-xs text-ink/65 mt-2 italic">{s.notes}</p>}
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* 360° tracking — admin sees raters list + results */}
      {data.tests.some((t) => t.test_template_id === "360-competencies") && (
        <section className="mt-12" data-testid="admin-360-section">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand mb-4">— Évaluation 360°</div>
          {data.tests.filter((t) => t.test_template_id === "360-competencies").map((t) => (
            <Raters360 key={t.id} assignmentId={t.id} scope="admin" showResults />
          ))}
        </section>
      )}

      <section className="mt-12" data-testid="admin-notes">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand mb-4">— Notes privées (admin)</div>
        <div className="bg-white border border-black/10 p-6">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={6}
            placeholder="Observations, points clés, sujets à creuser, axes de progrès…"
            className="w-full bg-transparent border border-black/10 px-4 py-3 text-sm focus:outline-none focus:border-brand resize-y"
            data-testid="admin-notes-textarea"
          />
          <div className="flex items-center justify-between mt-3">
            <div className="text-[10px] text-ink/45 font-mono">{notesUpdated ? `Mis à jour le ${fmtDt(notesUpdated)}` : "Non enregistré"}</div>
            <button onClick={saveNotes} disabled={savingNotes} data-testid="admin-notes-save" className="inline-flex items-center gap-2 bg-brand hover:bg-brandDark disabled:opacity-60 text-white px-4 py-2 text-[10px] uppercase tracking-[0.18em] font-mono">
              <Save size={12} /> {savingNotes ? "..." : "Enregistrer"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function Assignments() {
  const [coachees, setCoachees] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({ coachee_id: "", test_template_id: "" });

  useEffect(() => {
    api.get("/admin/coachees").then(({ data }) => setCoachees(data));
    api.get("/test-templates").then(({ data }) => setTemplates(data));
  }, []);

  const assign = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/assign-test", form);
      toast.success("Test assigné");
      setForm({ coachee_id: "", test_template_id: "" });
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  return (
    <div>
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand mb-4">— Tests</div>
      <h1 className="font-serif text-4xl md:text-5xl font-light text-navy mb-12">Assigner un test</h1>
      <form onSubmit={assign} data-testid="assign-form" className="border border-black/10 bg-white p-8 grid grid-cols-12 gap-6 max-w-3xl">
        <select required value={form.coachee_id} onChange={(e) => setForm({ ...form, coachee_id: e.target.value })} className="col-span-12 md:col-span-6 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" data-testid="assign-coachee">
          <option value="">Choisir un coaché</option>
          {coachees.map((c) => <option key={c.id} value={c.id}>{`${c.first_name || ""} ${c.name}`.trim()} — {c.email}</option>)}
        </select>
        <select required value={form.test_template_id} onChange={(e) => setForm({ ...form, test_template_id: e.target.value })} className="col-span-12 md:col-span-6 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" data-testid="assign-test">
          <option value="">Choisir un test</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.title_fr} ({t.question_count} q.)</option>)}
        </select>
        <button type="submit" className="col-span-12 bg-brand text-white px-5 py-3 text-xs uppercase tracking-[0.16em] font-mono w-fit">Assigner</button>
      </form>
    </div>
  );
}

function SessionsAdmin() {
  const [coachees, setCoachees] = useState([]);
  const [form, setForm] = useState({ coachee_id: "", title: "", starts_at: "", duration_minutes: 90, location: "", notes: "" });
  useEffect(() => { api.get("/admin/coachees").then(({ data }) => setCoachees(data)); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/sessions", { ...form, starts_at: new Date(form.starts_at).toISOString(), duration_minutes: parseInt(form.duration_minutes) });
      toast.success("Séance planifiée");
      setForm({ coachee_id: "", title: "", starts_at: "", duration_minutes: 90, location: "", notes: "" });
    } catch (e2) { toast.error(formatApiError(e2)); }
  };
  return (
    <div>
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand mb-4">— Agenda</div>
      <h1 className="font-serif text-4xl md:text-5xl font-light text-navy mb-12">Planifier une séance</h1>
      <form onSubmit={save} data-testid="session-form" className="border border-black/10 bg-white p-8 grid grid-cols-12 gap-6 max-w-3xl">
        <select required value={form.coachee_id} onChange={(e) => setForm({ ...form, coachee_id: e.target.value })} className="col-span-12 md:col-span-6 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand">
          <option value="">Coaché</option>
          {coachees.map((c) => <option key={c.id} value={c.id}>{`${c.first_name || ""} ${c.name}`.trim()}</option>)}
        </select>
        <input required placeholder="Intitulé" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="col-span-12 md:col-span-6 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" />
        <input required type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="col-span-12 md:col-span-6 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" />
        <input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="col-span-12 md:col-span-6 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" />
        <input placeholder="Lieu" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="col-span-12 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand" />
        <textarea placeholder="Notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="col-span-12 bg-transparent border-b border-ink/20 py-2 focus:outline-none focus:border-brand resize-none" />
        <button type="submit" className="col-span-12 bg-brand text-white px-5 py-3 text-xs uppercase tracking-[0.16em] font-mono w-fit">Planifier</button>
      </form>
    </div>
  );
}

function Messages() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/contact-messages").then(({ data }) => setItems(data)); }, []);
  return (
    <div>
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand mb-4">— Réception</div>
      <h1 className="font-serif text-4xl md:text-5xl font-light text-navy mb-12">Messages reçus</h1>
      <div className="divide-y divide-black/10 border border-black/10 bg-white">
        {items.length === 0 && <div className="p-8 text-ink/50">Aucun message</div>}
        {items.map((m) => (
          <div key={m.id} className="p-6 md:p-8" data-testid={`message-${m.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-serif text-xl text-navy">{m.name} <span className="text-ink/50 text-sm">— {m.email}</span></div>
                <div className="text-xs text-ink/50 mt-1">{m.topic} · {new Date(m.created_at).toLocaleString()}</div>
              </div>
              {m.company && <div className="text-xs text-ink/60 font-mono">{m.company}</div>}
            </div>
            <p className="text-ink/80 whitespace-pre-wrap mt-3">{m.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminSettings() {
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [domain, setDomain] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw !== pw2) return setErr("Les mots de passe ne correspondent pas.");
    if (pw.length < 8) return setErr("Min 8 caractères");
    setLoading(true);
    try { await api.post("/auth/change-password", { current_password: cur, new_password: pw }); toast.success("Mot de passe mis à jour"); setCur(""); setPw(""); setPw2(""); }
    catch (e2) { setErr(formatApiError(e2)); }
    finally { setLoading(false); }
  };
  useEffect(() => { api.get("/admin/email-status").then(({ data }) => setDomain(data)).catch(() => {}); }, []);
  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copié"); };

  return (
    <div data-testid="admin-settings">
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand mb-4">— Paramètres</div>
      <h1 className="font-serif text-4xl md:text-5xl font-light text-navy mb-10">Paramètres</h1>

      {/* Password change — placed first for quick access */}
      <section className="mb-12">
        <h2 className="font-serif text-2xl font-light text-navy mb-2">Mot de passe</h2>
        <p className="text-sm text-ink/55 mb-6">Modifiez votre mot de passe. Cliquez sur l'œil pour afficher la saisie.</p>
        <form onSubmit={submit} data-testid="admin-change-form" className="bg-white border border-black/10 p-8 md:p-10 max-w-xl space-y-7">
          <div><label className="block text-[10px] uppercase tracking-[0.25em] font-mono text-ink/55 mb-2">Mot de passe actuel</label><PasswordInput data-testid="admin-current" required value={cur} onChange={(e) => setCur(e.target.value)} /></div>
          <div><label className="block text-[10px] uppercase tracking-[0.25em] font-mono text-ink/55 mb-2">Nouveau mot de passe</label><PasswordInput data-testid="admin-new" required value={pw} onChange={(e) => setPw(e.target.value)} /></div>
          <div><label className="block text-[10px] uppercase tracking-[0.25em] font-mono text-ink/55 mb-2">Confirmer le mot de passe</label><PasswordInput data-testid="admin-confirm" required value={pw2} onChange={(e) => setPw2(e.target.value)} /></div>
          {err && <div className="text-sm text-destructive">{err}</div>}
          <button data-testid="admin-submit" disabled={loading} type="submit" className="bg-brand hover:bg-brandDark disabled:opacity-60 text-white px-7 py-4 text-xs uppercase tracking-[0.16em] font-mono">{loading ? "..." : "Mettre à jour"}</button>
        </form>
      </section>

      {/* Resend domain status */}
      {domain && (
        <section className="bg-white border border-black/10 p-8 mb-10" data-testid="resend-status">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand mb-2">— Domaine de messagerie</div>
              <h2 className="font-serif text-2xl text-navy font-light">{domain.domain || "audace-perspectives.com"}</h2>
            </div>
            <span className={`px-3 py-1 text-[10px] uppercase tracking-[0.18em] font-mono ${domain.verified ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"}`} data-testid="resend-badge">
              {domain.verified ? "✓ Vérifié" : "⚠︎ À vérifier"}
            </span>
          </div>

          {!domain.verified && (
            <div className="space-y-5 text-sm">
              <p className="text-ink/75 leading-relaxed">
                Tant que le domaine <span className="font-mono text-navy">audace-perspectives.com</span> n'est pas vérifié sur Resend, les emails ne sont délivrés qu'à <span className="font-mono text-navy">{domain.allowed_recipient || "aurianedepotesta@audace-perspectives.com"}</span>. C'est ce qui empêche les évaluateurs 360° et les nouveaux coachés de recevoir leurs invitations.
              </p>

              <ol className="list-decimal list-inside space-y-2 text-ink/80">
                <li>Se connecter sur <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-brand underline">resend.com/domains</a></li>
                <li>Cliquer <strong>Add Domain</strong> → saisir <span className="font-mono">audace-perspectives.com</span> → région <strong>EU (Frankfurt)</strong></li>
                <li>Ajouter les 3 enregistrements DNS suivants chez votre registrar (OVH, Gandi, Cloudflare…)</li>
              </ol>

              <div className="overflow-x-auto">
                <table className="w-full text-xs border border-black/10 mt-2" data-testid="dns-records">
                  <thead className="bg-ivory">
                    <tr><th className="p-2 text-left font-mono uppercase tracking-wide text-[10px]">Type</th><th className="p-2 text-left font-mono uppercase tracking-wide text-[10px]">Hôte</th><th className="p-2 text-left font-mono uppercase tracking-wide text-[10px]">Valeur (à recopier depuis Resend)</th></tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-black/5">
                      <td className="p-2 font-mono">MX</td>
                      <td className="p-2 font-mono">send</td>
                      <td className="p-2 font-mono break-all">feedback-smtp.eu-west-1.amazonses.com <span className="text-ink/45">(priorité 10)</span></td>
                    </tr>
                    <tr className="border-t border-black/5 bg-ivory/40">
                      <td className="p-2 font-mono">TXT (SPF)</td>
                      <td className="p-2 font-mono">send</td>
                      <td className="p-2 font-mono break-all">v=spf1 include:amazonses.com ~all</td>
                    </tr>
                    <tr className="border-t border-black/5">
                      <td className="p-2 font-mono">TXT (DKIM)</td>
                      <td className="p-2 font-mono">resend._domainkey</td>
                      <td className="p-2 font-mono text-ink/55">p=MIGfMA0GCSqG… <span className="text-ink/40 italic">(clé unique générée par Resend lors de l'ajout du domaine — à recopier depuis l'écran Resend)</span></td>
                    </tr>
                    <tr className="border-t border-black/5 bg-ivory/40">
                      <td className="p-2 font-mono">TXT (DMARC)</td>
                      <td className="p-2 font-mono">_dmarc</td>
                      <td className="p-2 font-mono break-all">v=DMARC1; p=none; rua=mailto:contact@audace-perspectives.com</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-ink/65 text-xs leading-relaxed">
                Une fois les DNS ajoutés, cliquer <strong>Verify DNS records</strong> dans Resend (propagation 5 à 30 min). Une fois vérifié, je bascule automatiquement le <code className="text-navy">SENDER_EMAIL</code> vers <span className="font-mono">contact@audace-perspectives.com</span> et tous les emails partiront depuis votre domaine.
              </p>

              <div className="flex flex-wrap gap-3 pt-2">
                <button onClick={() => copy("feedback-smtp.eu-west-1.amazonses.com")} className="text-[10px] uppercase tracking-[0.18em] font-mono border border-black/15 hover:border-brand hover:text-brand px-3 py-2">Copier valeur MX</button>
                <button onClick={() => copy("v=spf1 include:amazonses.com ~all")} className="text-[10px] uppercase tracking-[0.18em] font-mono border border-black/15 hover:border-brand hover:text-brand px-3 py-2">Copier SPF</button>
                <button onClick={() => copy("v=DMARC1; p=none; rua=mailto:contact@audace-perspectives.com")} className="text-[10px] uppercase tracking-[0.18em] font-mono border border-black/15 hover:border-brand hover:text-brand px-3 py-2">Copier DMARC</button>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  return (
    <Shell>
      <Routes>
        <Route index element={<Coachees />} />
        <Route path="coache/:id" element={<CoacheeDetail />} />
        <Route path="sessions" element={<SessionsAdmin />} />
        <Route path="assignments" element={<Assignments />} />
        <Route path="messages" element={<Messages />} />
        <Route path="parametres" element={<AdminSettings />} />
      </Routes>
    </Shell>
  );
}
